import { useState, useEffect, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, ArrowRight, Plane, Briefcase, MapPin, PartyPopper, Crown, Accessibility, GraduationCap, Package, PawPrint } from "lucide-react";
import { SERVICES, BOOKING_URL } from "@/data/site";

const iconMap = { Plane, Briefcase, MapPin, PartyPopper, Crown, Accessibility, GraduationCap, Package, PawPrint };

export const ServicesShowcase = () => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = SERVICES.length;

  const go = useCallback((n) => setIndex(((n % count) + count) % count), [count]);
  const next = useCallback(() => go(index + 1), [go, index]);
  const prev = useCallback(() => go(index - 1), [go, index]);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setIndex((p) => (p + 1) % count), 5000);
    return () => clearInterval(t);
  }, [paused, count]);

  const s = SERVICES[index];
  const Icon = iconMap[s.icon];

  return (
    <section id="services" className="bg-white py-24 md:py-28" data-testid="services-showcase">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-2xl mb-10">
          <p className="text-xs font-bold uppercase tracking-[0.2em] mb-4 text-brand">What we do</p>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-ink">Local taxis & private hire, every occasion</h2>
        </div>

        <div
          className="relative rounded-3xl overflow-hidden shadow-[0_30px_70px_-25px_rgba(0,0,0,0.35)]"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <div className="relative h-[440px] sm:h-[500px] lg:h-[560px]">
            <AnimatePresence mode="popLayout">
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 1.06 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                className="absolute inset-0 cursor-grab active:cursor-grabbing"
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.2}
                onDragEnd={(e, info) => {
                  if (info.offset.x < -80) next();
                  else if (info.offset.x > 80) prev();
                }}
                data-testid="service-slide-drag"
              >
                <img src={s.img} alt={s.title} className="h-full w-full object-cover pointer-events-none select-none" draggable="false" />
              </motion.div>
            </AnimatePresence>
            <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-ink via-ink/55 to-transparent lg:bg-gradient-to-r lg:from-ink/95 lg:via-ink/55 lg:to-transparent" />

            {/* content */}
            <div className="absolute inset-0 flex items-end lg:items-center pointer-events-none">
              <div className="p-8 sm:p-12 lg:p-16 max-w-xl">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -16 }}
                    transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                    data-testid={`service-slide-${index}`}
                  >
                    <span className="inline-flex items-center gap-2 rounded-full bg-brand text-white text-xs font-bold uppercase tracking-[0.15em] px-4 py-2 mb-5">
                      {Icon && <Icon className="h-4 w-4" strokeWidth={2} />} Service {String(index + 1).padStart(2, "0")}
                    </span>
                    <h3 className="font-heading text-3xl sm:text-4xl font-black text-white leading-tight">{s.title}</h3>
                    <p className="mt-4 text-white/75 leading-relaxed max-w-md">{s.desc}</p>
                    <a
                      href={BOOKING_URL}
                      data-testid="service-book-btn"
                      className="mt-7 inline-flex items-center gap-2 bg-white text-ink hover:bg-brand hover:text-white px-7 py-3.5 rounded-full font-bold transition-colors duration-300 pointer-events-auto"
                    >
                      Book this service <ArrowRight className="h-4 w-4" />
                    </a>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>

            {/* arrows */}
            <button
              onClick={prev}
              data-testid="service-prev-btn"
              aria-label="Previous service"
              className="absolute right-20 bottom-8 sm:top-8 sm:bottom-auto grid place-items-center h-12 w-12 rounded-full bg-white/15 ring-1 ring-white/30 text-white backdrop-blur hover:bg-brand hover:ring-brand transition-colors duration-300"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={next}
              data-testid="service-next-btn"
              aria-label="Next service"
              className="absolute right-6 bottom-8 sm:top-8 sm:bottom-auto grid place-items-center h-12 w-12 rounded-full bg-white/15 ring-1 ring-white/30 text-white backdrop-blur hover:bg-brand hover:ring-brand transition-colors duration-300"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* thumbnail tabs */}
        <div className="mt-6 flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-thin">
          {SERVICES.map((item, i) => {
            const TIcon = iconMap[item.icon];
            const active = i === index;
            return (
              <button
                key={item.title}
                onClick={() => go(i)}
                data-testid={`service-thumb-${i}`}
                aria-label={item.title}
                className={`group relative shrink-0 w-40 sm:w-44 h-24 rounded-xl overflow-hidden ring-2 transition-all duration-300 ${active ? "ring-brand scale-[1.02]" : "ring-transparent opacity-70 hover:opacity-100"}`}
              >
                <img src={item.img} alt={item.title} className="h-full w-full object-cover" draggable="false" />
                <span className={`absolute inset-0 transition-colors duration-300 ${active ? "bg-ink/30" : "bg-ink/55 group-hover:bg-ink/35"}`} />
                <span className="absolute inset-x-0 bottom-0 p-2.5 flex items-center gap-1.5 text-left">
                  {TIcon && <TIcon className={`h-3.5 w-3.5 shrink-0 ${active ? "text-brand-light" : "text-white"}`} strokeWidth={2} />}
                  <span className="text-[11px] font-bold leading-tight text-white line-clamp-2">{item.title}</span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};
